import React, { useState, useRef } from "react";
import { observer } from "mobx-react";

/**
 * Value input for an import condition.
 *  - inputType "chips"  : free-text chips, or restricted chips with a suggestion
 *                         list when `options` are provided (strings or {value,label})
 *  - inputType "number" : single numeric value
 *  - inputType "date"   : single date value (yyyy-mm-dd)
 */
const ConditionValuesInput = observer(props => {
  const {
    values = [],
    changeCbk = () => {},
    options = [],
    inputType = "chips",
    placeholder = "Enter one or more values"
  } = props || {};
  const [text, setText] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const normalizedOptions = (options || []).map(item =>
    typeof item === "string" ? { value: item, label: item } : item
  );
  const hasOptions = normalizedOptions.length > 0;
  const labelFor = value => {
    const match = normalizedOptions.find(option => option.value === value);
    return match ? match.label : value;
  };

  // ---- date range mode (Between) ----
  if (inputType === "daterange") {
    const updateBound = (position, value) => {
      const range = [values?.[0] || "", values?.[1] || ""];
      range[position] = value;
      changeCbk(range[0] || range[1] ? range : []);
    };
    return (
      <div className="conditionChipsInput dateRange">
        <input
          className="chipTextInput singleValueInput"
          type="date"
          value={values?.[0] || ""}
          max={values?.[1] || undefined}
          onChange={event => updateBound(0, event.currentTarget.value)}
        />
        <span className="dateRangeSep">to</span>
        <input
          className="chipTextInput singleValueInput"
          type="date"
          value={values?.[1] || ""}
          min={values?.[0] || undefined}
          onChange={event => updateBound(1, event.currentTarget.value)}
        />
      </div>
    );
  }

  // ---- single-value modes (numbers / dates) ----
  if (inputType === "number" || inputType === "date") {
    return (
      <div className="conditionChipsInput singleMode">
        <input
          className="chipTextInput singleValueInput"
          type={inputType}
          value={values?.[0] || ""}
          placeholder={inputType === "number" ? placeholder : ""}
          onChange={event => {
            const value = event.currentTarget.value;
            changeCbk(value ? [value] : []);
          }}
        />
      </div>
    );
  }

  // ---- chips mode ----
  const isSelected = value =>
    values.some(item => item?.toLowerCase() === value?.toLowerCase());

  const addValue = value => {
    const trimmed = value?.trim();
    if (trimmed && !isSelected(trimmed)) {
      changeCbk([...values, trimmed]);
    }
    setText("");
  };

  const commitText = () => {
    if (!text?.trim()) {
      return;
    }
    if (hasOptions) {
      // Restricted to CRM-provided options: commit the first label match
      const match = normalizedOptions.find(
        option =>
          !isSelected(option.value) &&
          option.label.toLowerCase().includes(text.trim().toLowerCase())
      );
      if (match) {
        addValue(match.value);
      }
    } else {
      addValue(text);
    }
  };

  const suggestions = hasOptions
    ? normalizedOptions.filter(
        option =>
          !isSelected(option.value) &&
          (!text?.trim() ||
            option.label.toLowerCase().includes(text.trim().toLowerCase()))
      )
    : [];

  const handleKeyDown = event => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitText();
    } else if (event.key === "Backspace" && !text && values?.length > 0) {
      changeCbk(values.slice(0, -1));
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handlePaste = event => {
    if (hasOptions) {
      return; // restricted fields: pick from the list
    }
    const pastedText = event.clipboardData?.getData("text") || "";
    if (pastedText.includes(",")) {
      event.preventDefault();
      const mergedList = [...values];
      pastedText
        .split(",")
        .map(item => item.trim())
        .filter(Boolean)
        .forEach(item => {
          if (!mergedList.some(v => v?.toLowerCase() === item.toLowerCase())) {
            mergedList.push(item);
          }
        });
      changeCbk(mergedList);
    }
  };

  const removeValue = index => {
    changeCbk(values.filter((_, idx) => idx !== index));
  };

  return (
    <div className="conditionChipsInput">
      {values?.length > 0 &&
        values.map((item, index) => (
          <span className="valueChip" key={`${item}-${index}`}>
            <span className="chipText" title={labelFor(item)}>
              {labelFor(item)}
            </span>
            <i
              className="material-icons chipRemove"
              onClick={() => removeValue(index)}
            >
              close
            </i>
          </span>
        ))}
      <input
        ref={inputRef}
        className="chipTextInput"
        type="text"
        value={text}
        placeholder={values?.length > 0 ? "" : placeholder}
        onChange={event => setText(event.currentTarget.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => hasOptions && setShowSuggestions(true)}
        onBlur={() => {
          // let option clicks land before closing
          setTimeout(() => setShowSuggestions(false), 150);
          if (!hasOptions) {
            commitText();
          }
        }}
        onPaste={handlePaste}
      />
      {hasOptions && showSuggestions && (
        <ul className="valueSuggestions">
          {suggestions?.length > 0 ? (
            suggestions.map(option => (
              <li
                key={option.value}
                className="suggestionItem"
                onMouseDown={event => {
                  event.preventDefault();
                  addValue(option.value);
                  if (inputRef?.current) {
                    inputRef.current.focus();
                  }
                }}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="suggestionEmpty">
              {values?.length >= normalizedOptions?.length
                ? "All values selected"
                : "No matching values"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

export { ConditionValuesInput };
export default ConditionValuesInput;
